import React from 'react';
import { Award, BadgeCheck, Users, MapPin, Star } from 'lucide-react';
import { whyChooseUs, testimonials, shopInfo } from '../data/mock';

const iconMap = {
  Award: Award,
  BadgeCheck: BadgeCheck,
  Users: Users,
  MapPin: MapPin
};

const AboutSection = () => {
  return (
    <section id="about" className="py-16 lg:py-24 bg-[#f7f5f2]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* About Content */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-20">
          <div className="relative">
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1697545806136-92d30cb7081e?w=600"
                alt="Our Store"
                className="w-full h-[400px] object-cover"
              />
            </div>
            {/* Experience Badge */}
            <div className="absolute -bottom-6 -right-6 bg-white rounded-xl p-6 shadow-lg border border-[#2563eb]/10">
              <div className="text-center">
                <div className="text-4xl font-bold text-[#2563eb]">24+</div>
                <div className="text-sm text-[#736c64]">Years of Trust</div>
              </div>
            </div>
            {/* Decorative */}
            <div className="absolute -top-4 -left-4 w-24 h-24 border-2 border-[#2563eb] rounded-xl -z-10"></div>
          </div>

          <div className="space-y-6">
            <span className="inline-block text-sm font-semibold text-[#2563eb] bg-white px-4 py-1.5 rounded-full">
              About Us
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#1e1919]">
              Your Local Trusted IT Partner
            </h2>
            <p className="text-[#736c64] leading-relaxed">
              Since 2001, {shopInfo.name} has been a trusted IT support and electronics solutions provider in Jassur, Himachal Pradesh, with over 24 years of industry experience. We offer reliable, efficient, and tailored technology solutions for individuals, small businesses, and organizations.
            </p>
            <p className="text-[#736c64] leading-relaxed">
              Our services range from computer hardware and software support to networking, system setup, and Canon printer solutions. As an official Canon Pixma Zone, we bring you genuine products with manufacturer warranty and expert after-sales support.
            </p>
            <div className="pt-4">
              <div className="flex items-center gap-4 p-4 bg-white rounded-xl border border-[#2563eb]/10">
                <div className="w-12 h-12 rounded-full bg-[#2563eb] flex items-center justify-center flex-shrink-0">
                  <BadgeCheck className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-[#1e1919]">Canon Pixma Zone</h4>
                  <p className="text-sm text-[#736c64]">Official dealer with genuine products and warranty</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Why Choose Us */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h3 className="text-2xl lg:text-3xl font-bold text-[#1e1919] mb-4">
              Why Choose Us?
            </h3>
            <p className="text-[#736c64] max-w-xl mx-auto">
              We're committed to providing the best IT support and electronics experience in Jassur.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, index) => {
              const IconComponent = iconMap[item.icon] || Award;
              return (
                <div
                  key={item.id}
                  className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-all duration-300 border border-transparent hover:border-[#2563eb]/20"
                >
                  <div className="w-14 h-14 rounded-full bg-[#2563eb]/10 flex items-center justify-center mx-auto mb-4">
                    <IconComponent className="w-7 h-7 text-[#2563eb]" />
                  </div>
                  <h4 className="font-semibold text-[#1e1919] mb-2">{item.title}</h4>
                  <p className="text-sm text-[#736c64]">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Testimonials */}
        <div>
          <div className="text-center mb-12">
            <span className="inline-block text-sm font-semibold text-[#2563eb] bg-white px-4 py-1.5 rounded-full mb-4">
              Customer Stories
            </span>
            <h3 className="text-2xl lg:text-3xl font-bold text-[#1e1919]">
              What Our Customers Say
            </h3>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 border border-[#2563eb]/10"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-[#f59e0b] text-[#f59e0b]" />
                  ))}
                </div>
                <p className="text-[#736c64] mb-4 italic">"{testimonial.content}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#2563eb] flex items-center justify-center">
                    <span className="text-white font-semibold text-sm">
                      {testimonial.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <div>
                    <h5 className="font-semibold text-[#1e1919] text-sm">{testimonial.name}</h5>
                    <p className="text-xs text-[#736c64]">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
